import { computed, ref } from 'vue'

/**
 * Theme-Umschaltung.
 *
 * Die Farbwerte liegen als CSS Custom Properties in `assets/css/app.css`:
 * `@theme` definiert die Hell-Variante, `:root[data-theme='dark']`
 * überschreibt dieselben Properties. Hier wird nur entschieden, welches
 * Attribut auf `<html>` steht.
 *
 * `system` wird bewusst schon hier zu `light`/`dark` aufgelöst und als
 * konkretes Attribut geschrieben. Der Alternativweg über
 * `@media (prefers-color-scheme: dark)` bräuchte den kompletten
 * Override-Block ein zweites Mal in der CSS-Datei – zwei Listen mit
 * ~70 Tokens, die auseinanderlaufen, sobald jemand nur eine davon pflegt.
 */
export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'smarthome:theme'

/** Muss mit dem Inline-Skript in `index.html` übereinstimmen (kein FOUC). */
const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: '#edf3f6',
  dark: '#0d141a',
}

function isPreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system'
}

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isPreference(stored) ? stored : 'system'
  } catch {
    // Privater Modus / blockierter Storage: Systemvorgabe ist die sichere Wahl.
    return 'system'
  }
}

const systemQuery: MediaQueryList | null =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null

// Modul-weiter Singleton-State: Alle Aufrufer teilen sich dieselbe Auswahl.
const preference = ref<ThemePreference>(readStoredPreference())
const systemPrefersDark = ref(systemQuery?.matches ?? false)

const resolvedTheme = computed<ResolvedTheme>(() => {
  if (preference.value !== 'system') return preference.value
  return systemPrefersDark.value ? 'dark' : 'light'
})

function applyTheme(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') return

  document.documentElement.setAttribute('data-theme', theme)

  const meta = document.querySelector('meta[name="theme-color"]')
  meta?.setAttribute('content', THEME_COLORS[theme])
}

function setTheme(next: ThemePreference): void {
  preference.value = next

  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // Nicht persistierbar – die Auswahl gilt dann nur für diese Sitzung.
  }

  applyTheme(resolvedTheme.value)
}

/** Reihenfolge des Umschalters: hell → dunkel → System → hell. */
function cycleTheme(): void {
  const order: ThemePreference[] = ['light', 'dark', 'system']
  const next = order[(order.indexOf(preference.value) + 1) % order.length]
  setTheme(next)
}

let initialized = false

/**
 * Einmalig beim App-Start aufrufen (siehe `main.ts`). Setzt das Attribut auf
 * den gespeicherten Wert und hält es bei Systemwechseln aktuell.
 */
export function initTheme(): void {
  if (initialized) return
  initialized = true

  applyTheme(resolvedTheme.value)

  systemQuery?.addEventListener('change', (event) => {
    systemPrefersDark.value = event.matches
    // Nur relevant, solange der Nutzer nicht selbst gewählt hat.
    if (preference.value === 'system') applyTheme(resolvedTheme.value)
  })
}

export function useTheme() {
  return { preference, resolvedTheme, setTheme, cycleTheme }
}
